import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  X,
  Calendar,
  User,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  AlertCircle,
  DollarSign,
  Building2,
  TrendingDown,
} from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import {
  approveRequestWithOverride,
  declineRequest,
} from '@/store/slices/resourceIntakeRequestsSlice';
import { Modal } from '@/components/ui';
import { GlassButton, GlassBadge } from '@/design-system';
import type {
  ResourceIntakeRequest,
  RequestType,
  RequestStatus,
  ResourceType,
  ValidationStatus,
} from '@/types';
import { UserRole } from '@/types';
import { formatCurrencyFull, formatPercent } from '@/utils/formatters';

interface RequestDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: ResourceIntakeRequest | null;
}

const RequestDetailModal: React.FC<RequestDetailModalProps> = ({
  isOpen,
  onClose,
  request,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { loading } = useSelector((state: RootState) => state.resourceIntakeRequests);

  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [overrideBudget, setOverrideBudget] = useState(false);

  if (!request) return null;

  const canApproveDecline = user && (user.role === UserRole.ADMIN || user.role === UserRole.EXECUTIVE);
  const isPending = request.status === 'SUBMITTED';

  const handleApprove = async () => {
    const result = await dispatch(
      approveRequestWithOverride({
        id: request.id,
        overrideBudgetWarning: overrideBudget,
      })
    );
    if (approveRequestWithOverride.fulfilled.match(result)) {
      setIsApproveModalOpen(false);
      setOverrideBudget(false);
      onClose();
    }
  };

  const handleDecline = async () => {
    const result = await dispatch(declineRequest({ id: request.id, reason: declineReason }));
    if (declineRequest.fulfilled.match(result)) {
      setIsDeclineModalOpen(false);
      setDeclineReason('');
      onClose();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: RequestStatus) => {
    const variantMap: Record<RequestStatus, 'warning' | 'success' | 'danger'> = {
      SUBMITTED: 'warning',
      APPROVED: 'success',
      DECLINED: 'danger',
    };

    const icons: Record<RequestStatus, React.ReactNode> = {
      SUBMITTED: <Clock className="w-4 h-4 mr-1.5" />,
      APPROVED: <CheckCircle className="w-4 h-4 mr-1.5" />,
      DECLINED: <XCircle className="w-4 h-4 mr-1.5" />,
    };

    return (
      <GlassBadge variant={variantMap[status]} size="md">
        {icons[status]}
        {status}
      </GlassBadge>
    );
  };

  const getValidationBadge = (status: ValidationStatus) => {
    const variantMap: Record<ValidationStatus, 'info' | 'success' | 'warning' | 'danger'> = {
      PENDING: 'info',
      VALID: 'success',
      WARNING: 'warning',
      INVALID: 'danger',
    };

    const icons: Record<ValidationStatus, React.ReactNode> = {
      PENDING: <Clock className="w-4 h-4 mr-1" />,
      VALID: <CheckCircle className="w-4 h-4 mr-1" />,
      WARNING: <AlertTriangle className="w-4 h-4 mr-1" />,
      INVALID: <AlertCircle className="w-4 h-4 mr-1" />,
    };

    const labels: Record<ValidationStatus, string> = {
      PENDING: 'Pending',
      VALID: 'Valid',
      WARNING: 'Warning',
      INVALID: 'Invalid',
    };

    return (
      <GlassBadge variant={variantMap[status]} size="sm">
        {icons[status]}
        {labels[status]}
      </GlassBadge>
    );
  };

  const getRequestTypeLabel = (type: RequestType) => {
    const labels: Record<RequestType, string> = {
      HEADCOUNT: 'Headcount (New Hire)',
      SOW: 'SOW (Statement of Work)',
      T_AND_M: 'T&M (Time and Materials)',
    };
    return labels[type];
  };

  const getResourceTypeLabel = (type: ResourceType) => {
    return type.replace('_', ' ');
  };

  const needsBudgetOverride = request.validationStatus === 'INVALID';

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="Request Details" size="xl">
        <div className="space-y-6">
          {/* Header with Status */}
          <div className="flex items-center justify-between pb-4 border-b">
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                {getRequestTypeLabel(request.requestType)}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Request ID: {request.id.slice(0, 8).toUpperCase()}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {getValidationBadge(request.validationStatus)}
              {getStatusBadge(request.status)}
            </div>
          </div>

          {/* Budget Validation Panel */}
          {request.validationStatus && request.validationStatus !== 'PENDING' && (
            <div className={`rounded-lg p-4 ${
              request.validationStatus === 'VALID' ? 'bg-status-success/10 border border-status-success/30' :
              request.validationStatus === 'WARNING' ? 'bg-status-warning/10 border border-status-warning/30' :
              'bg-status-error/10 border border-status-error/30'
            }`}>
              <div className="flex items-start gap-3">
                {request.validationStatus === 'VALID' && <CheckCircle className="w-5 h-5 text-status-success" />}
                {request.validationStatus === 'WARNING' && <AlertTriangle className="w-5 h-5 text-status-warning" />}
                {request.validationStatus === 'INVALID' && <AlertCircle className="w-5 h-5 text-status-error" />}
                <div className="flex-1">
                  <h4 className="font-medium text-sm">
                    {request.validationStatus === 'VALID' && 'Budget Validation Passed'}
                    {request.validationStatus === 'WARNING' && 'Budget Warning'}
                    {request.validationStatus === 'INVALID' && 'Budget Exceeded'}
                  </h4>
                  {request.validationNotes && (
                    <p className="text-sm mt-1 opacity-90">{request.validationNotes}</p>
                  )}
                  {request.budgetImpactPercent !== null && (
                    <div className="mt-2 flex items-center gap-4 text-xs">
                      <span className="flex items-center gap-1">
                        <TrendingDown className="w-3 h-3" />
                        Budget Impact: {formatPercent(request.budgetImpactPercent)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Request Details Grid */}
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase mb-1">
                  Description
                </label>
                <p className="text-sm text-foreground">{request.description}</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase mb-1">
                  Resource Type
                </label>
                <p className="text-sm text-foreground">
                  {getResourceTypeLabel(request.resourceType)}
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase mb-1">
                  Resource Level
                </label>
                <p className="text-sm text-foreground font-mono">
                  {request.resourceLevel?.displayName || request.resourceLevel?.code || 'N/A'}
                </p>
              </div>

              {request.program && (
                <div>
                  <label className="block text-xs font-medium text-muted-foreground uppercase mb-1">
                    Program
                  </label>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm text-foreground">{request.program.name}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground uppercase mb-1">
                    Estimated Annual Cost
                  </label>
                  <div className="flex items-center gap-1">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    <p className="text-lg font-semibold text-foreground">
                      {formatCurrencyFull(request.estimatedCost, { fallback: 'N/A' })}
                    </p>
                  </div>
                </div>

                {request.calculatedHourlyRate && (
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground uppercase mb-1">
                      Hourly Rate
                    </label>
                    <div className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4 text-muted-foreground" />
                      <p className="text-lg font-semibold text-foreground">
                        {formatCurrencyFull(Number(request.calculatedHourlyRate), { fallback: 'N/A' })}/hr
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase mb-1">
                  Requested By
                </label>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <p className="text-sm text-foreground">
                    {request.requestedBy?.firstName} {request.requestedBy?.lastName}
                  </p>
                </div>
                {request.requestedBy?.email && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {request.requestedBy.email}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase mb-1">
                  Submit Date
                </label>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <p className="text-sm text-foreground">
                    {formatDate(request.submitDate)}
                  </p>
                </div>
              </div>

              {request.approvedById && request.approvedDate && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground uppercase mb-1">
                      {request.status === 'APPROVED' ? 'Approved By' : 'Declined By'}
                    </label>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <p className="text-sm text-foreground">
                        {request.approvedBy?.firstName} {request.approvedBy?.lastName}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-muted-foreground uppercase mb-1">
                      {request.status === 'APPROVED' ? 'Approval Date' : 'Decline Date'}
                    </label>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <p className="text-sm text-foreground">
                        {formatDate(request.approvedDate)}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Business Justification */}
          <div className="pt-4 border-t border-border-primary">
            <label className="block text-xs font-medium text-muted-foreground uppercase mb-2">
              Business Justification
            </label>
            <div className="bg-slate-100 dark:bg-slate-800/50 rounded-lg p-4">
              <p className="text-sm text-foreground whitespace-pre-wrap">
                {request.businessJustification}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border-primary">
            {canApproveDecline && isPending && (
              <>
                <GlassButton
                  variant="secondary"
                  onClick={() => setIsDeclineModalOpen(true)}
                  leftIcon={<XCircle className="w-4 h-4" />}
                  className="text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                >
                  Decline
                </GlassButton>
                <GlassButton
                  variant="primary"
                  onClick={() => setIsApproveModalOpen(true)}
                  leftIcon={<CheckCircle className="w-4 h-4" />}
                  className="bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700"
                >
                  Approve
                </GlassButton>
              </>
            )}
            <GlassButton variant="secondary" onClick={onClose}>
              Close
            </GlassButton>
          </div>
        </div>
      </Modal>

      {/* Approve Confirmation */}
      <Modal
        isOpen={isApproveModalOpen}
        onClose={() => {
          setIsApproveModalOpen(false);
          setOverrideBudget(false);
        }}
        title="Approve Request"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-foreground">
            Are you sure you want to approve this {getRequestTypeLabel(request.requestType).toLowerCase()} request?
          </p>

          {needsBudgetOverride && (
            <div className="bg-status-error/10 border border-status-error/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-status-error flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-sm text-status-error">Budget Exceeded</h4>
                  <p className="text-xs mt-1 text-status-error/90">
                    {request.validationNotes}
                  </p>
                  <label className="flex items-center gap-2 mt-3">
                    <input
                      type="checkbox"
                      checked={overrideBudget}
                      onChange={(e) => setOverrideBudget(e.target.checked)}
                      className="rounded border-status-error"
                    />
                    <span className="text-sm text-status-error">
                      I understand and want to override the budget warning
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {!needsBudgetOverride && request.validationStatus === 'WARNING' && (
            <div className="bg-status-warning/10 border border-status-warning/30 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-status-warning flex-shrink-0" />
                <p className="text-xs text-status-warning">
                  {request.validationNotes}
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <GlassButton
              variant="secondary"
              onClick={() => {
                setIsApproveModalOpen(false);
                setOverrideBudget(false);
              }}
            >
              Cancel
            </GlassButton>
            <GlassButton
              variant="primary"
              onClick={handleApprove}
              disabled={loading || (needsBudgetOverride && !overrideBudget)}
              isLoading={loading}
              className="bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700"
            >
              Approve Request
            </GlassButton>
          </div>
        </div>
      </Modal>

      {/* Decline Confirmation */}
      <Modal
        isOpen={isDeclineModalOpen}
        onClose={() => {
          setIsDeclineModalOpen(false);
          setDeclineReason('');
        }}
        title="Decline Request"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-foreground">
            Are you sure you want to decline this {getRequestTypeLabel(request.requestType).toLowerCase()} request?
          </p>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Reason for declining (optional)
            </label>
            <textarea
              className="w-full px-3 py-2 text-sm border rounded-lg bg-glass/80 backdrop-blur-sm text-foreground shadow-ds-soft focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-accent-blue border-glass-border transition-all duration-200"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Provide a reason for declining this request..."
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <GlassButton
              variant="secondary"
              onClick={() => {
                setIsDeclineModalOpen(false);
                setDeclineReason('');
              }}
            >
              Cancel
            </GlassButton>
            <GlassButton
              variant="primary"
              onClick={handleDecline}
              disabled={loading}
              isLoading={loading}
              className="bg-gradient-to-br from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700"
            >
              Decline Request
            </GlassButton>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default RequestDetailModal;
